import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  IoCloudUploadOutline,
  IoPersonOutline,
  IoDocumentTextOutline,
} from "react-icons/io5";
import {
  FaFilePdf,
  FaFilePowerpoint,
  FaFileExcel,
  FaFileWord,
  FaCheckCircle,
} from "react-icons/fa";
import { saveReportFiles } from "../../../utils/fileStorage";

const AssignOriginalReportModal = ({
  show,
  onClose,
  initialEmail = "",
  initialReportTitle = "",
  initialCategory = "Market Intelligence",
  initialDestinationTab = "purchased",
}) => {
  const [assignedToEmail, setAssignedToEmail] = useState(initialEmail);
  const [reportTitle, setReportTitle] = useState(initialReportTitle);
  const [category, setCategory] = useState(initialCategory);
  const [destinationTab, setDestinationTab] = useState(
    initialDestinationTab || "purchased",
  );
  const fileType = "pdf";
  const [summary, setSummary] = useState("");
  const file = null;
  const [pdfFile, setPdfFile] = useState(null);
  const [pptFile, setPptFile] = useState(null);
  const [excelFile, setExcelFile] = useState(null);
  const [docxFile, setDocxFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Customer users dropdown states
  const [userList, setUserList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isManualEmail, setIsManualEmail] = useState(false);

  // Dynamic user reports dropdown states (by radio tab)
  const [availableReports, setAvailableReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [isManualTitle, setIsManualTitle] = useState(false);
  const [selectedReportKey, setSelectedReportKey] = useState("");
  const [selectedReportMeta, setSelectedReportMeta] = useState(null);

  useEffect(() => {
    if (initialEmail) setAssignedToEmail(initialEmail);
    if (initialReportTitle) setReportTitle(initialReportTitle);
    if (initialCategory) setCategory(initialCategory);
    if (initialDestinationTab) setDestinationTab(initialDestinationTab);
  }, [
    initialEmail,
    initialReportTitle,
    initialCategory,
    initialDestinationTab,
  ]);

  // Fetch registered customer users on modal open
  useEffect(() => {
    if (!show) return;

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
        const [docUsersRes, adminUsersRes] = await Promise.allSettled([
          axios.get(`${apiBase}/admin/user-documents/users`),
          axios.get(`${apiBase}/admin/users`),
        ]);

        const map = new Map();
        if (
          docUsersRes.status === "fulfilled" &&
          docUsersRes.value.data?.users &&
          Array.isArray(docUsersRes.value.data.users)
        ) {
          docUsersRes.value.data.users.forEach((u) => {
            if (u.email) {
              const emailKey = u.email.toLowerCase().trim();
              map.set(emailKey, {
                id: u.id,
                name: u.name || u.fullName || u.email,
                email: emailKey,
                company: u.company || "",
              });
            }
          });
        }

        if (
          adminUsersRes.status === "fulfilled" &&
          Array.isArray(adminUsersRes.value.data)
        ) {
          adminUsersRes.value.data.forEach((u) => {
            if (u.email) {
              const emailKey = u.email.toLowerCase().trim();
              if (!map.has(emailKey)) {
                map.set(emailKey, {
                  id: u.id,
                  name: u.fullName || u.name || u.email,
                  email: emailKey,
                  company: u.companyName || u.company || "",
                });
              }
            }
          });
        }

        // Also check localStorage for any customers
        try {
          const rawCustom = JSON.parse(
            localStorage.getItem("rwire_customized_reports") || "[]",
          );
          const rawAssigned = JSON.parse(
            localStorage.getItem("rwire_assigned_original_reports") || "[]",
          );
          [...rawCustom, ...rawAssigned].forEach((r) => {
            const email = (r.assignedToEmail || r.email || "")
              .toLowerCase()
              .trim();
            if (email && email.includes("@") && !map.has(email)) {
              map.set(email, {
                id: email,
                name: email.split("@")[0].replace(/[._-]/g, " "),
                email,
                company: "",
              });
            }
          });
        } catch (e) {}

        const usersArray = Array.from(map.values()).sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        setUserList(usersArray);

        // If initial email is provided and matches a user, keep select mode
        if (initialEmail) {
          const matched = usersArray.some(
            (u) => u.email === initialEmail.toLowerCase().trim(),
          );
          if (!matched) {
            setIsManualEmail(true);
          }
        }
      } catch (err) {
        console.warn(
          "Could not load users for AssignOriginalReportModal:",
          err,
        );
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [show, initialEmail]);

  // Fetch reports for the selected user & destination workspace tab
  const fetchReportsForUserAndTab = useCallback(async () => {
    if (!assignedToEmail || !assignedToEmail.includes("@")) {
      setAvailableReports([]);
      return;
    }

    setLoadingReports(true);
    const userEmailClean = assignedToEmail.toLowerCase().trim();
    const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";
    const reportMap = new Map();

    try {
      if (destinationTab === "customized") {
        // 1. Fetch from customized-reports backend endpoint
        const res = await axios
          .get(
            `${apiBase}/user/customized-reports?email=${encodeURIComponent(
              userEmailClean,
            )}`,
          )
          .catch(() => null);

        if (res?.data?.reports && Array.isArray(res.data.reports)) {
          res.data.reports.forEach((r) => {
            const title = (
              r.reportTitle ||
              r.title ||
              r.researchTopic ||
              ""
            ).trim();
            if (!title) return;

            const isSample = Boolean(
              r.isSampleRequest ||
              r.tag === "Request for Sample" ||
              r.source === "Sample Request" ||
              r.type === "sample_download" ||
              r.type === "sample" ||
              r.inquiryType === "sample_download" ||
              r.inquiryType === "sample" ||
              r.status === "Request for Sample"
            );

            let tagLabel = "Customized Request";
            if (isSample) {
              tagLabel = "Request for Sample";
            } else if (r.source === "Custom Research Request" || (r.id && String(r.id).startsWith("CR-"))) {
              tagLabel = "Custom Research";
            } else if (r.source === "Assigned Original Report" || (r.id && String(r.id).startsWith("ORD-"))) {
              tagLabel = "Customized Report";
            } else if (r.tag) {
              tagLabel = r.tag;
            }

            const cat = r.category || r.industryCategory || "";
            const catDisplay = cat && cat !== "N/A" && cat !== "Custom Research" && cat !== "Sample Research" ? ` (${cat})` : "";
            const displayLabel = `${title} — [${tagLabel}]${catDisplay}`;
            const reqKind = isSample ? "sample" : "custom";
            const mapKey = `${title.toLowerCase()}___${reqKind}`;

            if (!reportMap.has(mapKey)) {
              reportMap.set(mapKey, {
                id: r.id || `${title}_${reqKind}`,
                uniqueKey: mapKey,
                title,
                actualTitle: title,
                displayLabel,
                category: cat || (isSample ? "Sample Research" : "Custom Research"),
                tag: tagLabel,
                isSampleRequest: isSample,
                source: isSample ? "sample-request" : "backend-customized",
              });
            }
          });
        }

        // 2. Fetch from customer documents of type customized
        const docsRes = await axios
          .get(
            `${apiBase}/customer/documents?email=${encodeURIComponent(
              userEmailClean,
            )}&type=customized`,
          )
          .catch(() => null);

        if (docsRes?.data?.documents && Array.isArray(docsRes.data.documents)) {
          docsRes.data.documents.forEach((d) => {
            const title = (d.title || "").trim();
            if (!title) return;
            const isSample = title.toLowerCase().includes("sample") || Boolean(d.isSampleRequest);
            const reqKind = isSample ? "sample" : "custom";
            const mapKey = `${title.toLowerCase()}___${reqKind}`;
            if (!reportMap.has(mapKey)) {
              const tagLabel = isSample ? "Request for Sample" : "Customized Report";
              reportMap.set(mapKey, {
                id: d.id,
                uniqueKey: mapKey,
                title,
                actualTitle: title,
                displayLabel: `${title} — [${tagLabel}]`,
                category: "Customized Report",
                tag: tagLabel,
                isSampleRequest: isSample,
                source: "user-document",
              });
            }
          });
        }

        // 3. Enrich from localStorage customized reports
        try {
          const localCustom = JSON.parse(
            localStorage.getItem("rwire_customized_reports") || "[]",
          );
          const localAssigned = JSON.parse(
            localStorage.getItem("rwire_assigned_original_reports") || "[]",
          );
          [...localCustom, ...localAssigned].forEach((r) => {
            const rEmail = (r.assignedToEmail || r.email || "")
              .toLowerCase()
              .trim();
            if (!rEmail || rEmail === userEmailClean) {
              if (r.destinationTab === "customized" || !r.destinationTab) {
                const title = (
                  r.reportTitle ||
                  r.title ||
                  r.researchTopic ||
                  ""
                ).trim();
                if (!title) return;
                const isSample = Boolean(r.isSampleRequest || r.tag === "Request for Sample");
                const reqKind = isSample ? "sample" : "custom";
                const mapKey = `${title.toLowerCase()}___${reqKind}`;
                if (!reportMap.has(mapKey)) {
                  const tagLabel = isSample ? "Request for Sample" : "Customized Report";
                  reportMap.set(mapKey, {
                    id: r.id || `${title}_${reqKind}`,
                    uniqueKey: mapKey,
                    title,
                    actualTitle: title,
                    displayLabel: `${title} — [${tagLabel}]`,
                    category: r.category || (isSample ? "Sample Research" : "Custom Research"),
                    tag: tagLabel,
                    isSampleRequest: isSample,
                    source: "local-customized",
                  });
                }
              }
            }
          });
        } catch (e) {}
      } else if (destinationTab === "purchased") {
        // 1. Fetch user orders from backend
        const res = await axios
          .get(
            `${apiBase}/user/orders?email=${encodeURIComponent(
              userEmailClean,
            )}`,
          )
          .catch(() => null);

        if (res?.data?.orders && Array.isArray(res.data.orders)) {
          res.data.orders.forEach((o) => {
            const title = (o.reportTitle || o.title || "").trim();
            if (title && !reportMap.has(title.toLowerCase())) {
              const catDisplay = o.category ? ` (${o.category})` : "";
              reportMap.set(title.toLowerCase(), {
                id: o.id || title,
                uniqueKey: title.toLowerCase(),
                title,
                actualTitle: title,
                displayLabel: `${title}${catDisplay}`,
                category: o.category || "Market Intelligence",
                tag: "Purchased Report",
                isSampleRequest: false,
                source: "backend-purchased",
              });
            }
          });
        }

        // 2. Fetch from customer documents of type purchased
        const docsRes = await axios
          .get(
            `${apiBase}/customer/documents?email=${encodeURIComponent(
              userEmailClean,
            )}&type=purchased`,
          )
          .catch(() => null);

        if (docsRes?.data?.documents && Array.isArray(docsRes.data.documents)) {
          docsRes.data.documents.forEach((d) => {
            const title = (d.title || "").trim();
            if (title && !reportMap.has(title.toLowerCase())) {
              reportMap.set(title.toLowerCase(), {
                id: d.id,
                uniqueKey: title.toLowerCase(),
                title,
                actualTitle: title,
                displayLabel: `${title} (Document)`,
                category: "Market Intelligence",
                tag: "Purchased Report",
                isSampleRequest: false,
                source: "user-document",
              });
            }
          });
        }

        // 3. Enrich from localStorage assigned/purchased reports
        try {
          const localPurchased = JSON.parse(
            localStorage.getItem("rwire_purchased_reports") || "[]",
          );
          const localAssigned = JSON.parse(
            localStorage.getItem("rwire_assigned_original_reports") || "[]",
          );
          [...localPurchased, ...localAssigned].forEach((r) => {
            const rEmail = (r.assignedToEmail || r.email || "")
              .toLowerCase()
              .trim();
            if (!rEmail || rEmail === userEmailClean) {
              if (r.destinationTab === "purchased") {
                const title = (r.reportTitle || r.title || "").trim();
                if (title && !reportMap.has(title.toLowerCase())) {
                  reportMap.set(title.toLowerCase(), {
                    id: r.id || title,
                    uniqueKey: title.toLowerCase(),
                    title,
                    actualTitle: title,
                    displayLabel: `${title}${r.category ? ` (${r.category})` : ""}`,
                    category: r.category || "Market Intelligence",
                    tag: "Purchased Report",
                    isSampleRequest: false,
                    source: "local-purchased",
                  });
                }
              }
            }
          });
        } catch (e) {}
      } else if (destinationTab === "invoices") {
        // Fetch from orders
        const res = await axios
          .get(
            `${apiBase}/user/orders?email=${encodeURIComponent(
              userEmailClean,
            )}`,
          )
          .catch(() => null);

        if (res?.data?.orders && Array.isArray(res.data.orders)) {
          res.data.orders.forEach((o) => {
            const title = (
              o.reportTitle ||
              o.title ||
              o.invoiceNo ||
              ""
            ).trim();
            if (title && !reportMap.has(title.toLowerCase())) {
              const fullTitle = o.invoiceNo ? `${o.invoiceNo} — ${title}` : title;
              reportMap.set(title.toLowerCase(), {
                id: o.id || o.invoiceNo || title,
                uniqueKey: title.toLowerCase(),
                title: fullTitle,
                actualTitle: title,
                displayLabel: fullTitle,
                category: "Invoice",
                tag: "Invoice",
                isSampleRequest: false,
                source: "invoices",
              });
            }
          });
        }
      }
    } catch (err) {
      console.warn("Error loading user reports for dropdown:", err);
    } finally {
      const reports = Array.from(reportMap.values());
      setAvailableReports(reports);
      setLoadingReports(false);

      if (reports.length > 0) {
        setIsManualTitle(false);
        const targetTitle = (reportTitle || initialReportTitle || "").trim().toLowerCase();
        if (targetTitle) {
          const matched = reports.find(
            (r) =>
              r.title.toLowerCase() === targetTitle ||
              r.actualTitle?.toLowerCase() === targetTitle,
          );
          if (matched) {
            setSelectedReportKey(matched.uniqueKey);
            setReportTitle(matched.actualTitle || matched.title);
            setSelectedReportMeta(matched);
            if (matched.category) setCategory(matched.category);
          }
        }
      } else {
        setIsManualTitle(true);
      }
    }
  }, [assignedToEmail, destinationTab, reportTitle, initialReportTitle]);

  useEffect(() => {
    if (show && assignedToEmail && assignedToEmail.includes("@")) {
      fetchReportsForUserAndTab();
    } else {
      setAvailableReports([]);
    }
  }, [show, assignedToEmail, destinationTab, fetchReportsForUserAndTab]);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!assignedToEmail || !assignedToEmail.includes("@")) {
      setError("Please select or enter a valid user email address.");
      return;
    }

    if (!reportTitle.trim()) {
      setError("Please provide or select a report title.");
      return;
    }

    setLoading(true);

    try {
      const readAsDataUrl = (f) =>
        f
          ? new Promise((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(f);
            })
          : Promise.resolve(null);

      const [pdfUrl, pptUrl, excelUrl, docxUrl, mainUrl] = await Promise.all([
        readAsDataUrl(pdfFile),
        readAsDataUrl(pptFile),
        readAsDataUrl(excelFile),
        readAsDataUrl(docxFile),
        readAsDataUrl(file),
      ]);

      const formatFiles = {};
      if (pdfUrl) formatFiles.pdf = pdfUrl;
      if (pptUrl) formatFiles.ppt = pptUrl;
      if (excelUrl) formatFiles.excel = excelUrl;
      if (docxUrl) formatFiles.docx = docxUrl;
      if (mainUrl && !formatFiles[fileType]) formatFiles[fileType] = mainUrl;

      const formatFileNames = {};
      if (pdfFile) formatFileNames.pdf = pdfFile.name;
      if (pptFile) formatFileNames.ppt = pptFile.name;
      if (excelFile) formatFileNames.excel = excelFile.name;
      if (docxFile) formatFileNames.docx = docxFile.name;
      if (file) formatFileNames[fileType] = file.name;

      const activeFileName =
        (file && file.name) ||
        (fileType === "docx" && docxFile?.name) ||
        (fileType === "ppt" && pptFile?.name) ||
        (fileType === "excel" && excelFile?.name) ||
        (fileType === "pdf" && pdfFile?.name) ||
        docxFile?.name ||
        pptFile?.name ||
        excelFile?.name ||
        pdfFile?.name ||
        `${reportTitle.trim().replace(/[^a-zA-Z0-9_-]/g, "_")}.${fileType || "pdf"}`;

      const reportId = `ORIG-${Date.now()}`;
      const userEmailClean = assignedToEmail.trim().toLowerCase();
      const reportTitleClean = reportTitle.trim();

      // 1. Save heavy format file data URLs into IndexedDB (supports 1GB+, bypasses 5MB localStorage quota)
      await saveReportFiles(
        reportId,
        reportTitleClean,
        userEmailClean,
        formatFiles,
      );

      const isSample = Boolean(
        selectedReportMeta?.isSampleRequest ||
        selectedReportMeta?.tag === "Request for Sample" ||
        (destinationTab === "customized" && category && category.toLowerCase().includes("sample"))
      );

      const assignedReportObj = {
        id: reportId,
        reportTitle: reportTitleClean,
        title: reportTitleClean,
        category: category || "Market Intelligence",
        assignedToEmail: userEmailClean,
        email: userEmailClean,
        destinationTab: destinationTab,
        fileType: fileType,
        formatFiles: {}, // Keep empty in localStorage, files are retrieved from IndexedDB
        formatFileNames: formatFileNames,
        fileName: activeFileName,
        pdfUrl: null,
        pptUrl: null,
        excelUrl: null,
        docxUrl: null,
        fileUrl: null,
        fullReportUrl: null,
        summary:
          summary.trim() ||
          `Confidential original research report assigned exclusively to ${userEmailClean}. View-only access strictly enforced.`,
        assignedAt: new Date().toISOString().split("T")[0],
        date: new Date().toISOString().split("T")[0],
        isAssignedOriginal: true,
        viewOnly: true,
        amount: "300.00",
        license: "Single User License",
        status: "COMPLETED",
        invoiceNo: `INV-${Math.floor(100000 + Math.random() * 900000)}`,
        isSampleRequest: isSample,
        tag: isSample
          ? "Request for Sample"
          : (destinationTab === "customized" ? (selectedReportMeta?.tag || "Customized Request") : "Purchased Report"),
      };

      // Helper to strip bulky base64 data URLs from any existing items
      const sanitizeForLocalStorage = (rep) => {
        if (!rep || typeof rep !== "object") return rep;
        const {
          formatFiles: _ff,
          pdfUrl: _pdf,
          pptUrl: _ppt,
          excelUrl: _xl,
          docxUrl: _doc,
          fileUrl: _fu,
          fullReportUrl: _fru,
          ...rest
        } = rep;

        const cleanVal = (v) =>
          typeof v === "string" && (v.startsWith("data:") || v.length > 500)
            ? null
            : v;

        return {
          ...rest,
          pdfUrl: cleanVal(_pdf),
          pptUrl: cleanVal(_ppt),
          excelUrl: cleanVal(_xl),
          docxUrl: cleanVal(_doc),
          fileUrl: cleanVal(_fu),
          fullReportUrl: cleanVal(_fru),
          formatFiles: {},
        };
      };

      // 2. Safe LocalStorage Persistence
      try {
        const existing = JSON.parse(
          localStorage.getItem("rwire_assigned_original_reports") || "[]",
        );
        // Remove any previous entry for the same user and title to avoid stale/duplicate entries
        const cleanedExisting = (Array.isArray(existing) ? existing : [])
          .filter((r) => {
            const rTitle = (r.reportTitle || r.title || "")
              .toLowerCase()
              .trim();
            const rEmail = (r.assignedToEmail || r.email || "")
              .toLowerCase()
              .trim();
            return !(
              rTitle === reportTitleClean.toLowerCase() &&
              rEmail === userEmailClean
            );
          })
          .map(sanitizeForLocalStorage);

        const updated = [assignedReportObj, ...cleanedExisting];
        localStorage.setItem(
          "rwire_assigned_original_reports",
          JSON.stringify(updated),
        );
      } catch (lsErr) {
        console.warn("LocalStorage setItem quota warning:", lsErr);
      }

      // Maintain consistency between tabs: If assigned to customized, remove from purchased, and vice versa!
      if (destinationTab === "purchased") {
        try {
          const existingPurchased = JSON.parse(
            localStorage.getItem("rwire_purchased_reports") || "[]",
          );
          const sanitizedPurchased = (
            Array.isArray(existingPurchased) ? existingPurchased : []
          ).map(sanitizeForLocalStorage);
          localStorage.setItem(
            "rwire_purchased_reports",
            JSON.stringify([assignedReportObj, ...sanitizedPurchased]),
          );

          // Remove any mistaken entry from customized
          const existingCustom = JSON.parse(
            localStorage.getItem("rwire_customized_reports") || "[]",
          );
          const cleanedCustom = (
            Array.isArray(existingCustom) ? existingCustom : []
          ).filter((r) => {
            const rTitle = (r.reportTitle || r.title || "")
              .toLowerCase()
              .trim();
            const rEmail = (r.assignedToEmail || r.email || "")
              .toLowerCase()
              .trim();
            return !(
              rTitle === reportTitleClean.toLowerCase() &&
              rEmail === userEmailClean
            );
          });
          localStorage.setItem(
            "rwire_customized_reports",
            JSON.stringify(cleanedCustom),
          );
        } catch (e) {
          console.warn("LocalStorage setItem purchased reports warning:", e);
        }
      } else if (destinationTab === "customized") {
        try {
          const existingCustom = JSON.parse(
            localStorage.getItem("rwire_customized_reports") || "[]",
          );
          const sanitizedCustom = (
            Array.isArray(existingCustom) ? existingCustom : []
          ).map(sanitizeForLocalStorage);
          localStorage.setItem(
            "rwire_customized_reports",
            JSON.stringify([assignedReportObj, ...sanitizedCustom]),
          );

          // Clean up any mistaken entry from purchased reports
          const existingPurchased = JSON.parse(
            localStorage.getItem("rwire_purchased_reports") || "[]",
          );
          const cleanedPurchased = (
            Array.isArray(existingPurchased) ? existingPurchased : []
          ).filter((r) => {
            const rTitle = (r.reportTitle || r.title || "")
              .toLowerCase()
              .trim();
            const rEmail = (r.assignedToEmail || r.email || "")
              .toLowerCase()
              .trim();
            return !(
              rTitle === reportTitleClean.toLowerCase() &&
              rEmail === userEmailClean
            );
          });
          localStorage.setItem(
            "rwire_purchased_reports",
            JSON.stringify(cleanedPurchased),
          );
        } catch (e) {
          console.warn("LocalStorage setItem customized reports warning:", e);
        }
      } else if (destinationTab === "invoices") {
        try {
          const existingOrders = JSON.parse(
            localStorage.getItem("rwire_user_orders") || "[]",
          );
          const sanitizedOrders = (
            Array.isArray(existingOrders) ? existingOrders : []
          ).map(sanitizeForLocalStorage);
          localStorage.setItem(
            "rwire_user_orders",
            JSON.stringify([assignedReportObj, ...sanitizedOrders]),
          );
        } catch (e) {
          console.warn("LocalStorage setItem orders warning:", e);
        }
      }

      // 3. Dispatch custom events for real-time dashboard reactivity
      window.dispatchEvent(new Event("rwire_assigned_reports_updated"));
      window.dispatchEvent(new Event("rwire_purchased_reports_updated"));
      window.dispatchEvent(new Event("rwire_customized_reports_updated"));
      window.dispatchEvent(new Event("rwire_orders_updated"));

      // 4. Clean up any opposite duplicate document in backend user_documents
      const apiBase = process.env.REACT_APP_API_BASE_URL || "/api";

      // 5. Backend database persistence (upload actual files into user_documents table)
      const filesToUpload = [
        { file: pdfFile, type: "pdf" },
        { file: pptFile, type: "ppt" },
        { file: excelFile, type: "excel" },
        { file: docxFile, type: "docx" },
        { file: file, type: fileType },
      ].filter((item) => Boolean(item.file));

      const failedUploads = [];
      for (const item of filesToUpload) {
        try {
          const formData = new FormData();
          formData.append("user_email", userEmailClean);
          formData.append(
            "document_type",
            destinationTab === "invoices" ? "invoice" : destinationTab,
          );
          formData.append("title", reportTitleClean);
          formData.append("file", item.file);
          if (isSample) {
            formData.append("is_sample", "1");
            formData.append("tag", "Request for Sample");
          }

          await axios.post(`${apiBase}/admin/user-documents/upload`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (uploadErr) {
          const serverMsg = uploadErr.response?.data?.message || "";
          console.error(
            "Backend user-document upload failed for file:",
            item.file?.name,
            uploadErr,
          );
          failedUploads.push(
            (item.file?.name || item.type) +
              (serverMsg ? ` (${serverMsg})` : ""),
          );
        }
      }

      if (failedUploads.length > 0) {
        alert(
          "Warning: The following file(s) failed to upload: \n" +
            failedUploads.join("\n"),
        );
      }

      // Also sync metadata to assign-report
      try {
        await axios.post(`${apiBase}/admin/assign-report`, assignedReportObj);
      } catch (backendErr) {
        console.warn(
          "Backend API sync for assigned report skipped/failed",
          backendErr,
        );
      }

      setLoading(false);
      setSuccess(
        `Original report successfully assigned to ${assignedToEmail.trim()} under ${
          destinationTab === "customized"
            ? "Customized Reports"
            : destinationTab === "purchased"
              ? "Purchased Reports"
              : "Order & Invoice"
        } with View-Only access!`,
      );

      setTimeout(() => {
        onClose();
        setSuccess("");
      }, 1200);
    } catch (err) {
      console.error("Failed to assign report", err);
      setError(
        "An error occurred while assigning the report. Please try again.",
      );
      setLoading(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.6)", zIndex: 1060 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content rounded-4 border-0 shadow-lg overflow-hidden">
          {/* Header */}
          <div
            className="modal-header px-4 py-3 text-white d-flex align-items-center justify-content-between"
            style={{ backgroundColor: "#053b84" }}
          >
            <div className="d-flex align-items-center gap-2">
              <IoDocumentTextOutline size={22} />
              <h5 className="modal-title fw-bold mb-0">
                Assign Original Report (View-Only Access)
              </h5>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={onClose}
            ></button>
          </div>

          {/* Form Body */}
          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4">
              {error && (
                <div className="alert alert-danger py-2 small rounded-3 mb-3">
                  {error}
                </div>
              )}
              {success && (
                <div className="alert alert-success py-2 small rounded-3 mb-3 fw-semibold">
                  {success}
                </div>
              )}

              <div className="row g-3">
                {/* Destination Workspace Tab */}
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label fw-bold small text-dark mb-0">
                      Destination Workspace Tab{" "}
                      <span className="text-danger">*</span>
                    </label>
                    {assignedToEmail && (
                      <span
                        className="text-muted small"
                        style={{ fontSize: "0.78rem" }}
                      >
                        Selecting a tab loads that user's reports
                      </span>
                    )}
                  </div>
                  <div className="d-flex flex-wrap align-items-center gap-3 p-2 bg-light rounded-3 border">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="destinationTab"
                        id="destPurchased"
                        value="purchased"
                        checked={destinationTab === "purchased"}
                        onChange={(e) => {
                          setDestinationTab(e.target.value);
                          setSelectedReportKey("");
                          setSelectedReportMeta(null);
                          setReportTitle("");
                        }}
                      />
                      <label
                        className="form-check-label small fw-semibold text-dark"
                        htmlFor="destPurchased"
                      >
                        Purchased Reports
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="destinationTab"
                        id="destCustomized"
                        value="customized"
                        checked={destinationTab === "customized"}
                        onChange={(e) => {
                          setDestinationTab(e.target.value);
                          setSelectedReportKey("");
                          setSelectedReportMeta(null);
                          setReportTitle("");
                        }}
                      />
                      <label
                        className="form-check-label small fw-semibold text-dark"
                        htmlFor="destCustomized"
                      >
                        Customized Reports
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="destinationTab"
                        id="destInvoices"
                        value="invoices"
                        checked={destinationTab === "invoices"}
                        onChange={(e) => {
                          setDestinationTab(e.target.value);
                          setSelectedReportKey("");
                          setSelectedReportMeta(null);
                          setReportTitle("");
                        }}
                      />
                      <label
                        className="form-check-label small fw-semibold text-dark"
                        htmlFor="destInvoices"
                      >
                        Order & Invoice
                      </label>
                    </div>
                  </div>
                </div>

                {/* Target User Email (Dropdown with manual input fallback) */}
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label fw-bold small text-dark mb-0">
                      Assign To Customer Email{" "}
                      <span className="text-danger">*</span>
                    </label>
                    {userList.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none small text-primary"
                        style={{ fontSize: "0.78rem" }}
                        onClick={() => setIsManualEmail(!isManualEmail)}
                      >
                        {isManualEmail
                          ? "Select from user list"
                          : "+ Enter email manually"}
                      </button>
                    )}
                  </div>

                  {!isManualEmail && userList.length > 0 ? (
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <IoPersonOutline size={16} className="text-muted" />
                      </span>
                      <select
                        className="form-select"
                        value={assignedToEmail}
                        onChange={(e) => {
                          if (e.target.value === "__manual__") {
                            setIsManualEmail(true);
                            setAssignedToEmail("");
                            setSelectedReportKey("");
                            setSelectedReportMeta(null);
                            setReportTitle("");
                          } else {
                            setAssignedToEmail(e.target.value);
                            setSelectedReportKey("");
                            setSelectedReportMeta(null);
                            setReportTitle("");
                          }
                        }}
                        required
                      >
                        <option value="">
                          {loadingUsers
                            ? "Loading registered users..."
                            : "-- Select Customer / User --"}
                        </option>
                        {userList.map((u) => (
                          <option key={u.email} value={u.email}>
                            {u.name} ({u.email}){" "}
                            {u.company ? `— ${u.company}` : ""}
                          </option>
                        ))}
                        <option value="__manual__">
                          + Enter email manually...
                        </option>
                      </select>
                    </div>
                  ) : (
                    <div className="input-group">
                      <span className="input-group-text bg-white">
                        <IoPersonOutline size={16} className="text-muted" />
                      </span>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="e.g. user@researchwire.in"
                        value={assignedToEmail}
                        onChange={(e) => {
                          setAssignedToEmail(e.target.value);
                          setSelectedReportKey("");
                          setSelectedReportMeta(null);
                          setReportTitle("");
                        }}
                        required
                      />
                    </div>
                  )}
                </div>

                {/* Report Title (Dropdown with manual input fallback) */}
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <label className="form-label fw-bold small text-dark mb-0">
                      Report Title <span className="text-danger">*</span>
                    </label>
                    {availableReports.length > 0 && (
                      <button
                        type="button"
                        className="btn btn-link p-0 text-decoration-none small text-primary"
                        style={{ fontSize: "0.78rem" }}
                        onClick={() => {
                          const nextManual = !isManualTitle;
                          setIsManualTitle(nextManual);
                          if (nextManual) {
                            setSelectedReportKey("__custom__");
                          } else if (availableReports.length > 0 && !selectedReportKey) {
                            const first = availableReports[0];
                            setSelectedReportKey(first.uniqueKey);
                            setReportTitle(first.actualTitle || first.title);
                            setSelectedReportMeta(first);
                            if (first.category) setCategory(first.category);
                          }
                        }}
                      >
                        {isManualTitle
                          ? `Select from ${
                              destinationTab === "customized"
                                ? "customized / sample"
                                : destinationTab === "purchased"
                                  ? "purchased"
                                  : "order"
                            } reports (${availableReports.length})`
                          : "+ Enter custom title"}
                      </button>
                    )}
                  </div>

                  {!isManualTitle && availableReports.length > 0 ? (
                    <div className="position-relative">
                      <select
                        className="form-select"
                        value={selectedReportKey}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "__custom__") {
                            setIsManualTitle(true);
                            setSelectedReportKey("__custom__");
                            setReportTitle("");
                            setSelectedReportMeta(null);
                          } else {
                            const matched = availableReports.find(
                              (r) => r.uniqueKey === val,
                            );
                            setSelectedReportKey(val);
                            setReportTitle(matched?.actualTitle || matched?.title || val);
                            setSelectedReportMeta(matched || null);
                            if (matched?.category) {
                              setCategory(matched.category);
                            }
                          }
                        }}
                        required
                      >
                        <option value="">
                          {loadingReports
                            ? "Loading user's reports..."
                            : `-- Select ${
                                destinationTab === "customized"
                                  ? "Customized / Sample Report"
                                  : destinationTab === "purchased"
                                    ? "Purchased Report"
                                    : "Order / Invoice"
                              } (${availableReports.length} available) --`}
                        </option>
                        {availableReports.map((r) => (
                          <option key={r.uniqueKey} value={r.uniqueKey}>
                            {r.displayLabel || r.title}
                          </option>
                        ))}
                        <option value="__custom__">
                          + Enter custom / new report title...
                        </option>
                      </select>
                      {selectedReportMeta && (
                        <div className="mt-1 d-flex align-items-center gap-2">
                          <span
                            className="badge px-2 py-0.5 rounded-pill small fw-semibold"
                            style={{
                              backgroundColor: selectedReportMeta.isSampleRequest ? "#ede9fe" : "#e0f2fe",
                              color: selectedReportMeta.isSampleRequest ? "#5b21b6" : "#0369a1",
                              border: `1px solid ${selectedReportMeta.isSampleRequest ? "#ddd6fe" : "#bae6fd"}`,
                              fontSize: "0.74rem",
                            }}
                          >
                            {selectedReportMeta.tag || (selectedReportMeta.isSampleRequest ? "Request for Sample" : "Customized Request")}
                          </span>
                          {selectedReportMeta.category && selectedReportMeta.category !== "N/A" && (
                            <span className="text-muted small" style={{ fontSize: "0.75rem" }}>
                              Category: {selectedReportMeta.category}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder={
                          loadingReports
                            ? "Loading reports..."
                            : assignedToEmail
                              ? `e.g. ${
                                  destinationTab === "customized"
                                    ? "Green Hydrogen or Custom Topic"
                                    : "Global Semiconductor Market Strategic Report"
                                }`
                              : "Please select customer email first or enter title"
                        }
                        value={reportTitle}
                        onChange={(e) => setReportTitle(e.target.value)}
                        required
                      />
                      {assignedToEmail &&
                        availableReports.length === 0 &&
                        !loadingReports && (
                          <small className="text-muted d-block mt-1">
                            No existing {destinationTab} reports found for this
                            user. Type a new title above.
                          </small>
                        )}
                    </div>
                  )}
                </div>

                {/* 4 Multi-Format Attachments */}
                <div className="col-12">
                  <label className="form-label fw-bold small text-dark mb-2">
                    Attach Original Direct Files (PDF, PPT, Excel, Word)
                  </label>
                  <div className="row g-2">
                    {/* PDF */}
                    <div className="col-6 col-md-3">
                      <div className="p-2 border rounded bg-white text-center h-100 d-flex flex-column justify-content-between">
                        <div
                          className="fw-bold text-dark mb-1"
                          style={{ fontSize: "0.78rem" }}
                        >
                          1. PDF File
                        </div>
                        <label
                          htmlFor="pdfFileInput"
                          className={`btn btn-sm w-100 rounded-pill text-truncate d-flex align-items-center justify-content-center gap-1 ${
                            pdfFile
                              ? "btn-success text-white"
                              : "btn-outline-danger"
                          }`}
                          style={{ fontSize: "0.75rem", cursor: "pointer" }}
                          title={pdfFile ? pdfFile.name : "Choose PDF File"}
                        >
                          {pdfFile ? (
                            <FaCheckCircle size={11} />
                          ) : (
                            <FaFilePdf size={11} />
                          )}
                          <span className="text-truncate">
                            {pdfFile ? pdfFile.name : "Attach PDF"}
                          </span>
                        </label>
                        <input
                          id="pdfFileInput"
                          type="file"
                          className="d-none"
                          accept=".pdf"
                          onChange={(e) => setPdfFile(e.target.files[0])}
                        />
                        {pdfFile && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 border-0 text-decoration-none mt-1"
                            style={{ fontSize: "0.7rem" }}
                            onClick={() => setPdfFile(null)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    {/* PPT */}
                    <div className="col-6 col-md-3">
                      <div className="p-2 border rounded bg-white text-center h-100 d-flex flex-column justify-content-between">
                        <div
                          className="fw-bold text-dark mb-1"
                          style={{ fontSize: "0.78rem" }}
                        >
                          2. PPT File
                        </div>
                        <label
                          htmlFor="pptFileInput"
                          className={`btn btn-sm w-100 rounded-pill text-truncate d-flex align-items-center justify-content-center gap-1 ${
                            pptFile
                              ? "btn-success text-white"
                              : "btn-outline-warning text-dark"
                          }`}
                          style={{ fontSize: "0.75rem", cursor: "pointer" }}
                          title={pptFile ? pptFile.name : "Choose PPT File"}
                        >
                          {pptFile ? (
                            <FaCheckCircle size={11} />
                          ) : (
                            <FaFilePowerpoint size={11} />
                          )}
                          <span className="text-truncate">
                            {pptFile ? pptFile.name : "Attach PPT"}
                          </span>
                        </label>
                        <input
                          id="pptFileInput"
                          type="file"
                          className="d-none"
                          accept=".ppt,.pptx"
                          onChange={(e) => setPptFile(e.target.files[0])}
                        />
                        {pptFile && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 border-0 text-decoration-none mt-1"
                            style={{ fontSize: "0.7rem" }}
                            onClick={() => setPptFile(null)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Excel */}
                    <div className="col-6 col-md-3">
                      <div className="p-2 border rounded bg-white text-center h-100 d-flex flex-column justify-content-between">
                        <div
                          className="fw-bold text-dark mb-1"
                          style={{ fontSize: "0.78rem" }}
                        >
                          3. Excel Sheet
                        </div>
                        <label
                          htmlFor="excelFileInput"
                          className={`btn btn-sm w-100 rounded-pill text-truncate d-flex align-items-center justify-content-center gap-1 ${
                            excelFile
                              ? "btn-success text-white"
                              : "btn-outline-success"
                          }`}
                          style={{ fontSize: "0.75rem", cursor: "pointer" }}
                          title={
                            excelFile ? excelFile.name : "Choose Excel File"
                          }
                        >
                          {excelFile ? (
                            <FaCheckCircle size={11} />
                          ) : (
                            <FaFileExcel size={11} />
                          )}
                          <span className="text-truncate">
                            {excelFile ? excelFile.name : "Attach Excel"}
                          </span>
                        </label>
                        <input
                          id="excelFileInput"
                          type="file"
                          className="d-none"
                          accept=".xls,.xlsx,.csv"
                          onChange={(e) => setExcelFile(e.target.files[0])}
                        />
                        {excelFile && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 border-0 text-decoration-none mt-1"
                            style={{ fontSize: "0.7rem" }}
                            onClick={() => setExcelFile(null)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>

                    {/* DOCX */}
                    <div className="col-6 col-md-3">
                      <div className="p-2 border rounded bg-white text-center h-100 d-flex flex-column justify-content-between">
                        <div
                          className="fw-bold text-dark mb-1"
                          style={{ fontSize: "0.78rem" }}
                        >
                          4. Word (DOCX)
                        </div>
                        <label
                          htmlFor="docxFileInput"
                          className={`btn btn-sm w-100 rounded-pill text-truncate d-flex align-items-center justify-content-center gap-1 ${
                            docxFile
                              ? "btn-success text-white"
                              : "btn-outline-info"
                          }`}
                          style={{ fontSize: "0.75rem", cursor: "pointer" }}
                          title={docxFile ? docxFile.name : "Choose Word File"}
                        >
                          {docxFile ? (
                            <FaCheckCircle size={11} />
                          ) : (
                            <FaFileWord size={11} />
                          )}
                          <span className="text-truncate">
                            {docxFile ? docxFile.name : "Attach Word"}
                          </span>
                        </label>
                        <input
                          id="docxFileInput"
                          type="file"
                          className="d-none"
                          accept=".doc,.docx"
                          onChange={(e) => setDocxFile(e.target.files[0])}
                        />
                        {docxFile && (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 border-0 text-decoration-none mt-1"
                            style={{ fontSize: "0.7rem" }}
                            onClick={() => setDocxFile(null)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Report Summary / Executive Description */}
                <div className="col-12">
                  <label className="form-label fw-bold small text-dark mb-1">
                    Report Executive Summary / Notes
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Provide executive highlights or details for the user..."
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div className="modal-footer bg-light px-4 py-3">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill px-4"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white rounded-pill px-4 fw-semibold d-inline-flex align-items-center gap-2"
                style={{ backgroundColor: "#053b84" }}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    ></span>
                    Assigning...
                  </>
                ) : (
                  <>
                    <IoCloudUploadOutline size={18} /> Assign Report (View-Only)
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssignOriginalReportModal;
